import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Send,
  MoreVertical,
  Edit3,
  Trash2,
  Reply,
  Quote,
  AtSign,
  Bookmark,
  Flag,
  ThumbsUp,
  ThumbsDown,
  Check,
  X,
  User,
  Clock,
  ChevronDown,
  ChevronRight,
  Search,
  Filter,
  SortAsc,
  Edit2,
  Save,
} from 'lucide-react';

import { PageLayout } from '@/components/layout/PageLayout';
import { api } from '../services/client';

// Types
interface Comment {
  id: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  timestamp: Date;
  editedAt?: Date;
  replies: Comment[];
  reactions: { type: 'like' | 'dislike'; count: number; users: string[] };
  isPinned: boolean;
  isResolved: boolean;
  mentions: string[];
  context?: {
    type: 'tab' | 'session' | 'bookmark' | 'workspace';
    id: string;
    title: string;
  };
}

interface Thread {
  id: string;
  title: string;
  comments: Comment[];
  participants: string[];
  status: 'open' | 'resolved' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  tags: string[];
}

export const CommentingSystemPage: React.FC = () => {
  // State
  const [comments, setComments] = useState<Comment[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeTab, setActiveTab] = useState<'comments' | 'threads'>('comments');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'resolved'>('all');
  const [showResolved, setShowResolved] = useState(false);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [isReplying, setIsReplying] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // New comment state
  const [newComment, setNewComment] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');

  // Users for @mentions (populated from API)
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; name: string }>>([]);

  // Fetch comments on mount
  useEffect(() => {
    api.get('/comments')
      .then(r => setComments(r.data?.comments ?? r.data ?? []))
      .catch(console.error);
  }, []);

  // Filter comments
  const filteredComments = useMemo(() => {
    let result = comments;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.content.toLowerCase().includes(query) ||
        c.author.name.toLowerCase().includes(query)
      );
    }

    if (!showResolved) {
      result = result.filter(c => !c.isResolved);
    }

    return result;
  }, [comments, searchQuery, showResolved]);

  // Filter threads
  const filteredThreads = useMemo(() => {
    let result = threads;

    if (filterStatus !== 'all') {
      result = result.filter(t => t.status === filterStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.title.toLowerCase().includes(query) ||
        t.comments.some(c => c.content.toLowerCase().includes(query))
      );
    }

    return result;
  }, [threads, filterStatus, searchQuery]);

  // Format timestamp
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  };

  // Add comment
  const addComment = () => {
    if (!newComment.trim()) return;

    api.post('/comments', { text: newComment })
      .then(r => {
        setComments([r.data, ...comments]);
        setNewComment('');
      })
      .catch(console.error);
  };

  // Add reply
  const addReply = (parentId: string) => {
    if (!replyContent.trim()) return;

    const text = replyContent;

    api.post(`/comments/${parentId}/replies`, { text })
      .then(r => {
        const reply = r.data;
        const addReplyToComment = (comments: Comment[]): Comment[] => {
          return comments.map(c => {
            if (c.id === parentId) {
              return { ...c, replies: [...c.replies, reply] };
            }
            return { ...c, replies: addReplyToComment(c.replies) };
          });
        };
        setComments(addReplyToComment(comments));
        setIsReplying(null);
        setReplyContent('');
      })
      .catch(console.error);
  };

  // Edit comment
  const saveEdit = (commentId: string) => {
    const updateComment = (comments: Comment[]): Comment[] => {
      return comments.map(c => {
        if (c.id === commentId) {
          return { ...c, content: editContent, editedAt: new Date() };
        }
        return { ...c, replies: updateComment(c.replies) };
      });
    };

    setComments(updateComment(comments));
    setEditingComment(null);
    setEditContent('');
  };

  // Delete comment
  const deleteComment = (commentId: string) => {
    const removeComment = (comments: Comment[]): Comment[] => {
      return comments.filter(c => {
        if (c.id === commentId) return false;
        c.replies = removeComment(c.replies);
        return true;
      });
    };

    api.delete(`/comments/${commentId}`)
      .then(() => {
        setComments(removeComment(comments));
        setSelectedComment(null);
      })
      .catch(console.error);
  };

  // Toggle reaction
  const toggleReaction = (commentId: string, type: 'like' | 'dislike') => {
    const updateReactions = (comments: Comment[]): Comment[] => {
      return comments.map(c => {
        if (c.id === commentId) {
          const newCount = c.reactions.type === type ? c.reactions.count - 1 : c.reactions.count + 1;
          return { ...c, reactions: { ...c.reactions, type, count: newCount } };
        }
        return { ...c, replies: updateReactions(c.replies) };
      });
    };

    setComments(updateReactions(comments));
  };

  // Toggle resolve
  const toggleResolve = (commentId: string) => {
    const updateResolve = (comments: Comment[]): Comment[] => {
      return comments.map(c => {
        if (c.id === commentId) {
          return { ...c, isResolved: !c.isResolved };
        }
        return { ...c, replies: updateResolve(c.replies) };
      });
    };

    setComments(updateResolve(comments));
  };

  // Render comment with nested replies
  const renderComment = (comment: Comment, depth = 0) => (
    <motion.div
      key={comment.id}
      layout
      className={`${depth > 0 ? 'ml-8 border-l border-khiimori-border pl-4' : ''}`}
    >
      <div
        className={`bg-khiimori-bg-elevated rounded-xl p-4 mb-3 ${comment.isPinned ? 'border-l-2 border-purple-500' : ''}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-khiimori-text-primary">
                {comment.author.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-khiimori-text-primary">{comment.author.name}</span>
                {comment.isPinned && (
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">Pinned</span>
                )}
                {comment.isResolved && (
                  <span className="text-xs bg-success-500/20 text-success-400 px-2 py-0.5 rounded">Resolved</span>
                )}
              </div>
              <div className="text-xs text-khiimori-text-muted flex items-center gap-1">
                <Clock size={10} />
                {formatTimestamp(comment.timestamp)}
                {comment.editedAt && <span>(edited)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => toggleReaction(comment.id, 'like')}
              className="p-1.5 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors"
            >
              <ThumbsUp size={14} className={comment.reactions.type === 'like' && comment.reactions.count > 0 ? 'text-purple-400' : 'text-khiimori-text-muted'} />
            </button>
            {comment.reactions.count > 0 && (
              <span className="text-xs text-khiimori-text-muted">{comment.reactions.count}</span>
            )}
            <button className="p-1.5 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors">
              <MoreVertical size={14} className="text-khiimori-text-muted" />
            </button>
          </div>
        </div>

        {/* Content */}
        {editingComment === comment.id ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full px-3 py-2 bg-khiimori-bg-tertiary border border-khiimori-border rounded-lg text-khiimori-text-primary text-sm focus:outline-none focus:border-purple-500"
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={() => saveEdit(comment.id)}
                className="px-3 py-1 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-400 transition-colors flex items-center gap-1"
              >
                <Save size={12} />
                Save
              </button>
              <button
                onClick={() => setEditingComment(null)}
                className="px-3 py-1 bg-khiimori-bg-tertiary text-khiimori-text-secondary text-sm rounded-lg hover:bg-khiimori-bg-tertiary transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-khiimori-text-secondary text-sm mb-3">{comment.content}</p>
        )}

        {/* Context */}
        {comment.context && (
          <div className="flex items-center gap-2 text-xs text-khiimori-text-muted mb-3 bg-khiimori-bg-tertiary/50 rounded-lg px-3 py-2">
            <span>{comment.context.type}:</span>
            <span className="text-purple-400">{comment.context.title}</span>
          </div>
        )}

        {/* Actions */}
        {editingComment !== comment.id && (
          <div className="flex items-center gap-2 pt-2 border-t border-khiimori-border">
            <button
              onClick={() => setIsReplying(comment.id)}
              className="text-xs text-khiimori-text-muted hover:text-khiimori-text-primary transition-colors flex items-center gap-1"
            >
              <Reply size={12} />
              Reply
            </button>
            <button
              onClick={() => { setEditingComment(comment.id); setEditContent(comment.content); }}
              className="text-xs text-khiimori-text-muted hover:text-khiimori-text-primary transition-colors flex items-center gap-1"
            >
              <Edit2 size={12} />
              Edit
            </button>
            <button
              onClick={() => toggleResolve(comment.id)}
              className={`text-xs transition-colors flex items-center gap-1 ${comment.isResolved ? 'text-success-400' : 'text-khiimori-text-muted hover:text-white'}`}
            >
              <Check size={12} />
              {comment.isResolved ? 'Unresolve' : 'Resolve'}
            </button>
            <button
              onClick={() => deleteComment(comment.id)}
              className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 ml-auto"
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>
        )}

        {/* Reply input */}
        {isReplying === comment.id && (
          <div className="mt-3 space-y-2">
            <textarea
              ref={textareaRef}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              className="w-full px-3 py-2 bg-khiimori-bg-tertiary border border-khiimori-border rounded-lg text-khiimori-text-primary text-sm focus:outline-none focus:border-purple-500"
              rows={2}
            />
            <div className="flex gap-2">
              <button
                onClick={() => addReply(comment.id)}
                disabled={!replyContent.trim()}
                className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reply
              </button>
              <button
                onClick={() => { setIsReplying(null); setReplyContent(''); }}
                className="px-3 py-1.5 bg-khiimori-bg-tertiary text-khiimori-text-secondary text-sm rounded-lg hover:bg-khiimori-bg-tertiary transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Nested replies */}
      {comment.replies.map(reply => renderComment(reply, depth + 1))}
    </motion.div>
  );

  return (
    <PageLayout
      title="Comments"
      description="Collaborate with comments on tabs, sessions, and more"
      icon={MessageSquare}
    >
      <div className="bg-khiimori-bg-secondary rounded-xl overflow-hidden">
      {/* Header */}
      <div className="border-b border-khiimori-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowResolved(!showResolved)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showResolved ? 'bg-primary-500 text-white' : 'bg-khiimori-bg-elevated text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary'
              }`}
            >
              <Filter size={16} />
              Show Resolved
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search comments..."
              className="w-full pl-10 pr-4 py-2 bg-khiimori-bg-elevated border border-khiimori-border rounded-lg text-khiimori-text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-4 mt-4 border-b border-khiimori-border">
          <button
            onClick={() => setActiveTab('comments')}
            className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'comments'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-khiimori-text-muted hover:text-khiimori-text-secondary'
            }`}
          >
            <MessageSquare size={14} />
            Comments
            <span className="bg-khiimori-bg-elevated text-khiimori-text-muted text-xs px-2 py-0.5 rounded-full">{comments.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('threads')}
            className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'threads'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-khiimori-text-muted hover:text-khiimori-text-secondary'
            }`}
          >
            <Filter size={14} />
            Threads
            <span className="bg-khiimori-bg-elevated text-khiimori-text-muted text-xs px-2 py-0.5 rounded-full">{threads.length}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-auto p-6">
        <AnimatePresence mode="wait">
          {activeTab === 'comments' && (
            <motion.div
              key="comments"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* New comment input */}
              <div className="bg-khiimori-bg-elevated rounded-xl p-4 border border-khiimori-border">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment... Use @ to mention someone"
                  className="w-full px-3 py-2 bg-khiimori-bg-tertiary border border-khiimori-border rounded-lg text-khiimori-text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                  rows={3}
                />
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <button className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors">
                      <AtSign size={16} className="text-khiimori-text-muted" />
                    </button>
                    <button className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors">
                      <Quote size={16} className="text-khiimori-text-muted" />
                    </button>
                  </div>
                  <button
                    onClick={addComment}
                    disabled={!newComment.trim()}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send size={16} />
                    Post
                  </button>
                </div>
              </div>

              {/* Comments list */}
              {filteredComments.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare size={48} className="mx-auto text-khiimori-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-khiimori-text-secondary">No comments yet</h3>
                  <p className="text-khiimori-text-muted mt-2">Start a conversation by adding a comment</p>
                </div>
              ) : (
                filteredComments.map(comment => renderComment(comment))
              )}
            </motion.div>
          )}

          {activeTab === 'threads' && (
            <motion.div
              key="threads"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="space-y-4">
                {filteredThreads.map(thread => (
                  <motion.div
                    key={thread.id}
                    layout
                    className="bg-khiimori-bg-elevated rounded-xl p-4 border border-khiimori-border hover:border-khiimori-border transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-khiimori-text-primary">{thread.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            thread.status === 'open' ? 'bg-primary-500/20 text-primary-400' :
                            thread.status === 'resolved' ? 'bg-success-500/20 text-success-400' :
                            'bg-khiimori-bg-tertiary/20 text-khiimori-text-muted'
                          }`}>
                            {thread.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-sm text-khiimori-text-muted">
                          <span className="flex items-center gap-1">
                            <MessageSquare size={12} />
                            {thread.comments.length} comments
                          </span>
                          <span>•</span>
                          <span>{thread.participants.length} participants</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          {thread.tags.map(tag => (
                            <span key={tag} className="text-xs bg-khiimori-bg-tertiary text-khiimori-text-secondary px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg transition-colors">
                        <ChevronRight size={16} className="text-khiimori-text-muted" />
                      </button>
                    </div>
                  </motion.div>
                ))}

                {filteredThreads.length === 0 && (
                  <div className="text-center py-12">
                    <Filter size={48} className="mx-auto text-khiimori-text-muted mb-4" />
                    <h3 className="text-lg font-medium text-khiimori-text-secondary">No threads found</h3>
                    <p className="text-khiimori-text-muted mt-2">Create a thread to organize your discussions</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </PageLayout>
  );
};